import type {
  BrandSpecial,
  StoreSpecial as V3StoreSpecial,
} from '@iheartjane/data-access';
import { composeFilters } from '@iheartjane/menu';
import type { Nullable, QualifyingStatusEnum } from '@iheartjane/types';

import type { MenuCategory } from '@jane/kiosk/util';
import {
  buildRootTypeFilter,
  buildSpecialFilter,
  buildStoreFilter,
} from '@jane/kiosk/util';
import type { SearchProps, AlgoliaProduct } from '@jane/search/data-access';
/* eslint-disable-next-line no-restricted-imports */
import type { StoreSpecial } from '@jane/shared/models';
import type { StoreSpecial as ZStoreSpecial } from '@jane/shared/types';

interface SearchOptionsArgs {
  brandSpecial?: Nullable<BrandSpecial>;
  categories?: Array<MenuCategory | undefined>;
  /**
   * Adds a static filter for custom row categories. This will be used in place of the root type filter
   * created from a selected `category`.
   */
  customRowFilter?: string;
  /**
   * Optionally order search results to promote certain products, usually a brand's products.
   * This is mostly important when searching and displaying menu products directly, rather than when obtaining facets for filters.
   * @see https://www.algolia.com/doc/guides/managing-results/rules/merchandising-and-promoting/in-depth/optional-filters/
   */
  optionalFilters?: string;
  /**
   * The relevant QualifyingStatus if a store or brand special is in scope.
   */
  qualifyingStatus?: Nullable<QualifyingStatusEnum>;
  storeId: number;
  storeSpecial?: Nullable<StoreSpecial | ZStoreSpecial | V3StoreSpecial>;
}

export const searchOptions = ({
  brandSpecial,
  categories,
  customRowFilter,
  optionalFilters,
  qualifyingStatus,
  storeId,
  storeSpecial,
}: SearchOptionsArgs): Pick<
  SearchProps<AlgoliaProduct>,
  'options' | 'staticFilters'
> => ({
  options: {
    optionalFilters,
  },
  staticFilters: composeFilters(
    buildStoreFilter(storeId),
    customRowFilter,
    buildSpecialFilter({
      special: storeSpecial,
      qualifyingStatus,
    }),
    buildSpecialFilter({
      isBrandSpecial: !!brandSpecial,
      special: brandSpecial,
      qualifyingStatus,
    }),
    buildRootTypeFilter(categories)
  ),
});
